import { useState, type ReactNode } from 'react';
import { useCustom, useCustomMutation } from '@refinedev/core';
import { BarChart3, Check, ExternalLink, MessageSquareText, Plus, RefreshCw } from 'lucide-react';
import { notify } from '@/lib/notify';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';
import {
  INTERACTION_TYPE_META,
  getInteractionResponseLabel,
  type InteractionType,
} from './interactions-shared';

type InteractionStatus = 'ACTIVE' | 'CLOSED';

interface InteractionOption {
  id: string;
  label: string;
}

export interface Interaction {
  // livestream: number; breakout: string (uuid)
  id: string | number;
  type: InteractionType;
  title: string;
  description: string | null;
  status: InteractionStatus;
  isAnonymous: boolean;
  allowMultipleOptions: boolean;
  allowMultipleSubmissions: boolean;
  needAnswer: boolean;
  options: InteractionOption[];
  optionStats: Array<InteractionOption & { count: number }>;
  totalResponses: number;
}

export interface InteractionResponse {
  id: string | number;
  userId: number | null;
  selectedOptionIds: string[];
  textValue: string | null;
  isAnswered: boolean;
  createdAt: string;
  user: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    username: string | null;
    email?: string | null;
    telegramId?: string | null;
  } | null;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

interface CustomQueryData<T> {
  data: T | ApiEnvelope<T>;
}

function isApiEnvelope<T>(payload: T | ApiEnvelope<T>): payload is ApiEnvelope<T> {
  return typeof payload === 'object' && payload !== null && 'success' in payload && 'data' in payload;
}

function getCustomData<T>(queryData: CustomQueryData<T> | undefined, fallback: T): T {
  if (!queryData) {
    return fallback;
  }
  return isApiEnvelope(queryData.data) ? queryData.data.data : queryData.data;
}

const dateFormatter = new Intl.DateTimeFormat('ru-RU', {
  dateStyle: 'long',
  timeStyle: 'short',
});

function formatDateTime(value: string | null): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return dateFormatter.format(date);
}

export interface InteractionsAdminProps {
  /**
   * Базовый путь ресурса без хвоста `/interactions`.
   * Эфир: `/admin/livestreams/6`
   * Breakout-зал: `/admin/breakout/livestreams/6`
   */
  baseUrl: string;
  title?: string;
  description?: string;
  /** Подмешивается в values при создании интерактива (для breakout — targetRoomIndex). */
  createExtraValues?: Record<string, unknown>;
  /**
   * bare — без внешней Card-рамки: компонент рендерится плоско, чтобы
   * встраиваться внутрь модалки (модалка = контейнер, без «карточки
   * в карточке»).
   */
  bare?: boolean;
}

export function InteractionsAdmin({
  baseUrl,
  title = 'Интерактивы трансляции',
  description = 'Опросы и сборы ответов живут отдельно от модерации чата, чтобы оператор не терял рабочее пространство.',
  createExtraValues,
  bare = false,
}: InteractionsAdminProps) {
  const [type, setType] = useState<InteractionType>('POLL');
  const [interactionTitle, setInteractionTitle] = useState('');
  const [interactionDescription, setInteractionDescription] = useState('');
  const [optionsText, setOptionsText] = useState('Да\nНет');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [allowMultipleOptions, setAllowMultipleOptions] = useState(false);
  const [allowMultipleSubmissions, setAllowMultipleSubmissions] = useState(false);
  const [needAnswer, setNeedAnswer] = useState(false);
  const [selectedInteractionId, setSelectedInteractionId] = useState<string | number | null>(null);

  const interactionsQuery = useCustom<ApiEnvelope<Interaction[]> | Interaction[]>({
    url: `${baseUrl}/interactions`,
    method: 'get',
    // WS push: livestream.interaction.changed → invalidate.
    // refetchInterval — safety-net на случай разрыва WS.
    queryOptions: { refetchInterval: 5 * 60_000 },
  });
  const interactions = getCustomData(interactionsQuery.query.data, []);
  const selectedInteraction = interactions.find((item) => item.id === selectedInteractionId) ?? interactions[0] ?? null;

  const responsesQuery = useCustom<ApiEnvelope<InteractionResponse[]> | InteractionResponse[]>({
    url: `${baseUrl}/interactions/${selectedInteraction?.id ?? 0}/responses`,
    method: 'get',
    queryOptions: {
      enabled: selectedInteraction !== null,
      // WS push: livestream.interaction.changed (с interactionId) → invalidate
      // именно текущего интерактива. refetchInterval — safety-net.
      refetchInterval: 5 * 60_000,
    },
  });
  const responses = getCustomData(responsesQuery.query.data, []);
  const createMutation = useCustomMutation();
  const statusMutation = useCustomMutation();
  const answerMutation = useCustomMutation();
  const [answeringResponseId, setAnsweringResponseId] = useState<string | number | null>(null);
  const activeInteractions = interactions.filter((interaction) => interaction.status === 'ACTIVE').length;
  const totalResponses = interactions.reduce((sum, interaction) => sum + interaction.totalResponses, 0);
  const canTrackAnsweredResponses = selectedInteraction?.type === 'QUESTION_COLLECTION' && selectedInteraction.needAnswer;
  const unansweredResponseCount = canTrackAnsweredResponses
    ? responses.filter((response) => !response.isAnswered).length
    : 0;
  const answeredResponseCount = canTrackAnsweredResponses
    ? responses.length - unansweredResponseCount
    : 0;
  const typeMeta = INTERACTION_TYPE_META[type];
  const anonymousCheckboxId = `${baseUrl}-interaction-anonymous`;
  const multipleOptionsCheckboxId = `${baseUrl}-interaction-multiple-options`;
  const multipleSubmissionsCheckboxId = `${baseUrl}-interaction-multiple-submissions`;
  const needAnswerCheckboxId = `${baseUrl}-interaction-need-answer`;

  const resetForm = () => {
    setInteractionTitle('');
    setInteractionDescription('');
    setOptionsText('Да\nНет');
    setIsAnonymous(false);
    setAllowMultipleOptions(false);
    setAllowMultipleSubmissions(false);
    setNeedAnswer(false);
  };

  const handleInteractionTypeChange = (value: string) => {
    if (value !== 'POLL' && value !== 'QUESTION_COLLECTION') {
      return;
    }

    setType(value);

    if (value === 'QUESTION_COLLECTION') {
      setAllowMultipleOptions(false);
    } else {
      setNeedAnswer(false);
    }
  };

  const handleCreateInteraction = () => {
    const normalizedTitle = interactionTitle.trim();
    if (!normalizedTitle) {
      notify.error('Введите название интерактива');
      return;
    }
    const options = optionsText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((label, index) => ({ id: `option-${index + 1}`, label }));
    if (type === 'POLL' && options.length < 2) {
      notify.error('Для голосования нужно минимум два варианта');
      return;
    }

    createMutation.mutate({
      url: `${baseUrl}/interactions`,
      method: 'post',
      values: {
        type,
        title: normalizedTitle,
        description: interactionDescription.trim() || null,
        isAnonymous,
        allowMultipleOptions,
        allowMultipleSubmissions,
        needAnswer: type === 'QUESTION_COLLECTION' && needAnswer,
        ...(type === 'POLL' ? { options } : {}),
        ...(createExtraValues ? createExtraValues : {}),
      },
    }, {
      onSuccess: () => {
        notify.success('Интерактив создан');
        resetForm();
        interactionsQuery.query.refetch();
      },
      onError: (error) => notify.error(error.message || 'Не удалось создать интерактив'),
    });
  };

  const handleToggleStatus = (interaction: Interaction) => {
    const action = interaction.status === 'ACTIVE' ? 'close' : 'reopen';
    statusMutation.mutate({
      url: `${baseUrl}/interactions/${interaction.id}/${action}`,
      method: 'post',
      values: {},
    }, {
      onSuccess: () => interactionsQuery.query.refetch(),
      onError: (error) => notify.error(error.message || 'Не удалось изменить статус'),
    });
  };

  const handleToggleResponseAnswered = (response: InteractionResponse) => {
    if (!selectedInteraction || selectedInteraction.type !== 'QUESTION_COLLECTION' || !selectedInteraction.needAnswer) {
      return;
    }

    const nextAnswered = !response.isAnswered;
    setAnsweringResponseId(response.id);
    answerMutation.mutate({
      url: `${baseUrl}/interactions/${selectedInteraction.id}/responses/${response.id}/answered`,
      method: 'post',
      values: { isAnswered: nextAnswered },
    }, {
      onSuccess: () => {
        notify.success(nextAnswered ? 'Ответ отмечен отвеченным' : 'Ответ вернули в новые');
        responsesQuery.query.refetch();
      },
      onError: (error) => notify.error(error.message || 'Не удалось обновить ответ'),
      onSettled: () => setAnsweringResponseId(null),
    });
  };

  // bare: модалка сама контейнер — рендерим плоско без Card-рамки,
  // сохраняя тот же layout (header-блок + grid контента).
  const Shell = bare
    ? ({ children }: { children: ReactNode }) => <div>{children}</div>
    : ({ children }: { children: ReactNode }) => (
        <Card className="overflow-hidden">{children}</Card>
      );
  const ShellHeader = bare
    ? ({ children }: { children: ReactNode }) => (
        <div className="pb-4">{children}</div>
      )
    : ({ children }: { children: ReactNode }) => (
        <CardHeader className="border-b pb-4">{children}</CardHeader>
      );
  const ShellContent = bare
    ? ({ children }: { children: ReactNode }) => (
        <div className="grid gap-4 pt-4 xl:grid-cols-[420px_minmax(0,1fr)]">
          {children}
        </div>
      )
    : ({ children }: { children: ReactNode }) => (
        <CardContent className="grid gap-4 pt-4 xl:grid-cols-[420px_minmax(0,1fr)]">
          {children}
        </CardContent>
      );

  return (
    <Shell>
      <ShellHeader>
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex flex-col gap-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <BarChart3 className="h-4 w-4" />
              {title}
            </CardTitle>
            <CardDescription>
              {description}
            </CardDescription>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:flex sm:flex-wrap sm:justify-end">
            <div className="rounded-lg border bg-background px-3 py-2">
              <div className="text-[11px] uppercase tracking-normal text-muted-foreground">Всего</div>
              <div className="text-lg font-semibold tabular-nums">{interactions.length}</div>
            </div>
            <div className="rounded-lg border bg-background px-3 py-2">
              <div className="text-[11px] uppercase tracking-normal text-muted-foreground">Активно</div>
              <div className="text-lg font-semibold tabular-nums">{activeInteractions}</div>
            </div>
            <div className="rounded-lg border bg-background px-3 py-2">
              <div className="text-[11px] uppercase tracking-normal text-muted-foreground">Ответов</div>
              <div className="text-lg font-semibold tabular-nums">{totalResponses}</div>
            </div>
          </div>
        </div>
      </ShellHeader>
      <ShellContent>
        <section className="flex flex-col gap-4 rounded-lg border bg-muted/20 p-4">
          <div className="flex flex-col gap-1">
            <h3 className="text-sm font-medium">Новый интерактив</h3>
            <p className="text-xs text-muted-foreground">{typeMeta.description}</p>
          </div>

          <ToggleGroup
            type="single"
            value={type}
            onValueChange={handleInteractionTypeChange}
            className="grid h-auto w-full grid-cols-2"
          >
            <ToggleGroupItem value="POLL" className="h-auto justify-start gap-2 px-3 py-2">
              <BarChart3 className="h-4 w-4" />
              Голосование
            </ToggleGroupItem>
            <ToggleGroupItem value="QUESTION_COLLECTION" className="h-auto justify-start gap-2 px-3 py-2">
              <MessageSquareText className="h-4 w-4" />
              Сбор ответов
            </ToggleGroupItem>
          </ToggleGroup>

          <div className="flex flex-col gap-3">
            <Input
              value={interactionTitle}
              onChange={(event) => setInteractionTitle(event.target.value)}
              placeholder="Название интерактива"
            />
            <Textarea
              value={interactionDescription}
              onChange={(event) => setInteractionDescription(event.target.value)}
              placeholder="Короткое описание для участников"
              className="min-h-20 resize-none"
            />
          </div>

          {type === 'POLL' && (
            <Textarea
              value={optionsText}
              onChange={(event) => setOptionsText(event.target.value)}
              placeholder="Каждый вариант с новой строки"
              className="min-h-32 resize-none"
            />
          )}

          <div className="flex flex-col gap-2">
            <div className="flex items-start gap-3 rounded-lg border bg-background p-3 text-sm">
              <Checkbox
                id={anonymousCheckboxId}
                checked={isAnonymous}
                onCheckedChange={(checked) => setIsAnonymous(checked === true)}
                className="mt-0.5"
              />
              <label htmlFor={anonymousCheckboxId} className="flex cursor-pointer flex-col gap-0.5">
                <span className="font-medium">Анонимно для участников</span>
                <span className="text-xs text-muted-foreground">В ответах не будет видно имя участника.</span>
              </label>
            </div>
            {type === 'POLL' && (
              <div className="flex items-start gap-3 rounded-lg border bg-background p-3 text-sm">
                <Checkbox
                  id={multipleOptionsCheckboxId}
                  checked={allowMultipleOptions}
                  onCheckedChange={(checked) => setAllowMultipleOptions(checked === true)}
                  className="mt-0.5"
                />
                <label htmlFor={multipleOptionsCheckboxId} className="flex cursor-pointer flex-col gap-0.5">
                  <span className="font-medium">Несколько вариантов</span>
                  <span className="text-xs text-muted-foreground">Участник сможет выбрать больше одного ответа.</span>
                </label>
              </div>
            )}
            <div className="flex items-start gap-3 rounded-lg border bg-background p-3 text-sm">
              <Checkbox
                id={multipleSubmissionsCheckboxId}
                checked={allowMultipleSubmissions}
                onCheckedChange={(checked) => setAllowMultipleSubmissions(checked === true)}
                className="mt-0.5"
              />
              <label htmlFor={multipleSubmissionsCheckboxId} className="flex cursor-pointer flex-col gap-0.5">
                <span className="font-medium">Повторные отправки</span>
                <span className="text-xs text-muted-foreground">Участник сможет отправить ответ повторно.</span>
              </label>
            </div>
            {type === 'QUESTION_COLLECTION' && (
              <div className="flex items-start gap-3 rounded-lg border bg-background p-3 text-sm">
                <Checkbox
                  id={needAnswerCheckboxId}
                  checked={needAnswer}
                  onCheckedChange={(checked) => setNeedAnswer(checked === true)}
                  className="mt-0.5"
                />
                <label htmlFor={needAnswerCheckboxId} className="flex cursor-pointer flex-col gap-0.5">
                  <span className="font-medium">Нужно ответить</span>
                  <span className="text-xs text-muted-foreground">
                    В админке появится статус и кнопка, чтобы отмечать такие ответы обработанными.
                  </span>
                </label>
              </div>
            )}
          </div>

          <Button
            className="w-full"
            onClick={handleCreateInteraction}
            disabled={createMutation.mutation.isPending}
          >
            {createMutation.mutation.isPending ? <RefreshCw className="animate-spin" /> : <Plus />}
            Создать интерактив
          </Button>
        </section>

        <section className="grid min-h-[560px] gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
          <div className="flex min-w-0 flex-col gap-3 rounded-lg border bg-muted/20 p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex flex-col gap-1">
                <h3 className="text-sm font-medium">Список</h3>
                <p className="text-xs text-muted-foreground">Автообновление каждые 10 секунд</p>
              </div>
              {interactionsQuery.query.isFetching && (
                <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>

            {interactionsQuery.query.isLoading ? (
              <div className="flex min-h-48 items-center justify-center rounded-lg border bg-background text-sm text-muted-foreground">
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Загружаем интерактивы...
              </div>
            ) : (interactions.length === 0 ? (
              <div className="flex min-h-48 flex-col items-center justify-center gap-2 rounded-lg border border-dashed bg-background p-4 text-center">
                <BarChart3 className="h-5 w-5 text-muted-foreground" />
                <div className="text-sm font-medium">Интерактивов пока нет</div>
                <div className="max-w-56 text-xs text-muted-foreground">
                  Создайте голосование или сбор ответов, и он появится у участников трансляции.
                </div>
              </div>
            ) : (
              <div className="flex max-h-[620px] flex-col gap-2 overflow-auto pr-1">
                {interactions.map((interaction) => {
                  const isSelected = selectedInteraction?.id === interaction.id;
                  const interactionMeta = INTERACTION_TYPE_META[interaction.type];

                  return (
                    <button
                      key={interaction.id}
                      type="button"
                      className={cn(
                        'w-full rounded-lg border bg-background p-3 text-left text-sm transition-colors',
                        isSelected ? 'border-primary bg-muted shadow-sm' : 'hover:bg-muted/60',
                      )}
                      onClick={() => setSelectedInteractionId(interaction.id)}
                    >
                      <div className="flex min-w-0 items-start justify-between gap-2">
                        <div className="flex min-w-0 flex-col gap-1">
                          <span className="truncate font-medium">{interaction.title}</span>
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            {interaction.type === 'POLL' ? (
                              <BarChart3 className="h-3 w-3" />
                            ) : (
                              <MessageSquareText className="h-3 w-3" />
                            )}
                            {interactionMeta.label}
                          </span>
                        </div>
                        <Badge variant={interaction.status === 'ACTIVE' ? 'default' : 'secondary'} className="shrink-0">
                          {interaction.status === 'ACTIVE' ? 'Активен' : 'Закрыт'}
                        </Badge>
                      </div>
                      <div className="mt-3 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                        <span>{getInteractionResponseLabel(interaction.totalResponses)}</span>
                        <span>{interaction.isAnonymous ? 'Анонимно' : 'Публично'}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          <div className="min-w-0 rounded-lg border bg-background p-4">
            {selectedInteraction ? (
              <div className="flex h-full min-h-[520px] flex-col gap-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex min-w-0 flex-col gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="min-w-0 truncate text-base font-semibold">{selectedInteraction.title}</h3>
                      <Badge variant={selectedInteraction.status === 'ACTIVE' ? 'default' : 'secondary'}>
                        {selectedInteraction.status === 'ACTIVE' ? 'Активен' : 'Закрыт'}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline">{INTERACTION_TYPE_META[selectedInteraction.type].label}</Badge>
                      <Badge variant="outline">
                        {selectedInteraction.isAnonymous ? 'Анонимно' : 'Публично'}
                      </Badge>
                      {selectedInteraction.allowMultipleOptions && <Badge variant="outline">Мультивыбор</Badge>}
                      {selectedInteraction.allowMultipleSubmissions && <Badge variant="outline">Повторные ответы</Badge>}
                      {selectedInteraction.needAnswer && <Badge variant="outline">Нужно ответить</Badge>}
                    </div>
                    {selectedInteraction.description && (
                      <p className="text-sm text-muted-foreground">{selectedInteraction.description}</p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleToggleStatus(selectedInteraction)}
                    disabled={statusMutation.mutation.isPending}
                    className="shrink-0"
                  >
                    {statusMutation.mutation.isPending && <RefreshCw className="animate-spin" />}
                    {selectedInteraction.status === 'ACTIVE' ? 'Закрыть' : 'Открыть'}
                  </Button>
                </div>

                {selectedInteraction.type === 'POLL' && (
                  <div className="flex flex-col gap-3 rounded-lg border bg-muted/20 p-3">
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <span className="font-medium">Результаты голосования</span>
                      <span className="text-muted-foreground">
                        {getInteractionResponseLabel(selectedInteraction.totalResponses)}
                      </span>
                    </div>
                    <div className="flex flex-col gap-3">
                      {selectedInteraction.optionStats.map((option) => {
                        const percentage = selectedInteraction.totalResponses > 0
                          ? Math.round((option.count / selectedInteraction.totalResponses) * 100)
                          : 0;

                        return (
                          <div key={option.id} className="flex flex-col gap-1.5">
                            <div className="flex items-center justify-between gap-3 text-xs">
                              <span className="min-w-0 truncate font-medium">{option.label}</span>
                              <span className="shrink-0 text-muted-foreground">
                                {option.count} · {percentage}%
                              </span>
                            </div>
                            <div className="h-2 overflow-hidden rounded-full bg-muted">
                              <div className="h-full rounded-full bg-primary" style={{ width: `${percentage}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="flex min-h-0 flex-1 flex-col gap-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="text-sm font-medium">
                        Последние ответы
                      </h4>
                      <Badge variant="secondary">{responses.length}</Badge>
                      {canTrackAnsweredResponses && responses.length > 0 && (
                        <>
                          <Badge variant={unansweredResponseCount > 0 ? 'default' : 'secondary'}>
                            Новые: {unansweredResponseCount}
                          </Badge>
                          <Badge variant="outline">Отвечено: {answeredResponseCount}</Badge>
                        </>
                      )}
                    </div>
                    {responsesQuery.query.isFetching && (
                      <RefreshCw className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
                    )}
                  </div>

                  {responsesQuery.query.isLoading ? (
                    <div className="flex min-h-48 items-center justify-center rounded-lg border text-sm text-muted-foreground">
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Загружаем ответы...
                    </div>
                  ) : (responses.length === 0 ? (
                    <div className="flex min-h-48 items-center justify-center rounded-lg border border-dashed text-sm text-muted-foreground">
                      Ответов пока нет.
                    </div>
                  ) : (
                    <div className="flex max-h-[360px] flex-col gap-2 overflow-auto pr-1">
                      {responses.map((response) => {
                        const userName = response.user
                          ? [response.user.firstName, response.user.lastName].filter(Boolean).join(' ')
                            || response.user.username
                            || response.user.email
                            || (response.user.telegramId ? `Telegram ${response.user.telegramId}` : null)
                            || `#${response.user.id}`
                          : `Пользователь #${response.userId ?? '—'}`;
                        const userProfileId = response.user?.id ?? response.userId;
                        const canOpenUserProfile = userProfileId !== null && userProfileId !== undefined;
                        const selectedLabels = response.selectedOptionIds
                          .map((optionId) => (
                            selectedInteraction.options.find((option) => option.id === optionId)?.label ?? optionId
                          ))
                          .join(', ');

                        return (
                          <div
                            key={response.id}
                            className={cn(
                              'rounded-lg border p-3 text-sm transition-colors',
                              canTrackAnsweredResponses && response.isAnswered
                                ? 'border-muted bg-muted/20 text-muted-foreground'
                                : 'bg-muted/30',
                            )}
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                              {canOpenUserProfile ? (
                                <a
                                  href={`/users/show/${userProfileId}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex min-w-0 max-w-full items-center gap-1 font-medium text-foreground transition-colors hover:text-primary hover:underline"
                                  title="Открыть пользователя в новой вкладке"
                                >
                                  <span className="truncate">{userName}</span>
                                  <ExternalLink className="h-3 w-3 shrink-0" />
                                </a>
                              ) : (
                                <span className="font-medium text-foreground">{userName}</span>
                              )}
                              <span>{formatDateTime(response.createdAt)}</span>
                            </div>
                            <div className="mt-2 whitespace-pre-wrap">
                              {response.textValue || selectedLabels || '—'}
                            </div>
                            {canTrackAnsweredResponses && (
                              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t pt-3">
                                <Badge variant={response.isAnswered ? 'secondary' : 'default'}>
                                  {response.isAnswered ? 'Отвечено' : 'Новый ответ'}
                                </Badge>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant={response.isAnswered ? 'outline' : 'secondary'}
                                  onClick={() => handleToggleResponseAnswered(response)}
                                  disabled={answeringResponseId === response.id}
                                  className="h-8"
                                >
                                  {answeringResponseId === response.id ? (
                                    <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <Check className="h-3.5 w-3.5" />
                                  )}
                                  {response.isAnswered ? 'Вернуть в новые' : 'Отвечено'}
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex min-h-[520px] flex-col items-center justify-center gap-2 text-center">
                <MessageSquareText className="h-6 w-6 text-muted-foreground" />
                <div className="text-sm font-medium">Выберите интерактив</div>
                <div className="max-w-72 text-xs text-muted-foreground">
                  Здесь появятся настройки, результаты и ответы выбранного интерактива.
                </div>
              </div>
            )}
          </div>
        </section>
      </ShellContent>
    </Shell>
  );
}
